import {
  EVENTO_INTERNO,
  NS_INTERNO,
  type CanalResumo,
  type EventoDigitandoInterno,
  type EventoLeituraInterna,
  type EventoMensagemInterna,
  type EventoPresencaInterna,
} from '@mymaxx/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { create } from 'zustand';
import { Telefone } from '../paginas/ChamadaDeVoz';
import { internoApi } from './interno-api';
import { useSessao } from './sessao';
import { tocarAvisoDeMensagem } from './som';
import { tokenParaHandshake } from './tempo-real';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const ContextoInterno = createContext<Socket | null>(null);

/**
 * Qual conversa interna está aberta na tela, se houver.
 *
 * O provedor precisa disso para não tocar o aviso da mensagem que a pessoa está vendo
 * chegar. Fica num estado global porque quem sabe é a tela e quem pergunta é a moldura.
 */
export const useCanalAberto = create<{
  canalId: string | null;
  definir: (id: string | null) => void;
}>((set) => ({ canalId: null, definir: (canalId) => set({ canalId }) }));

/**
 * Uma conexão da conversa interna para o painel inteiro, viva enquanto a pessoa está logada.
 *
 * Não é detalhe de organização: **estar conectado aqui é o que significa "está com o
 * MyMAXX aberto"**, e é daí que sai a bolinha de presença que o resto da equipe vê. Se o
 * socket vivesse só dentro da tela de Conversas, a pessoa apareceria offline para todo
 * mundo enquanto atendesse cliente — que é onde ela passa o dia.
 *
 * De quebra, é o que faz o contador do menu acender e o som tocar estando em qualquer tela.
 */
export function ProvedorInterno({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const usuarioId = useSessao((e) => e.usuario?.id);
  const qc = useQueryClient();

  useEffect(() => {
    if (!usuarioId) return;
    const s: Socket = io(`${BASE_URL}${NS_INTERNO}`, {
      // Os dois transportes, como no socket do atendimento: atrás de proxy (o do Vite em
      // desenvolvimento, o nginx em produção) a subida direta para WebSocket às vezes
      // falha, e sem o polling de reserva o canal simplesmente não conecta.
      transports: ['websocket', 'polling'],
      // O token é pedido a cada handshake, e renovado se estiver para vencer. Congelá-lo
      // faria a reconexão depois de 15 minutos ser recusada para sempre, com a aba aberta
      // e sem receber mais nada — o defeito que o chat teve em 28/09.
      auth: (entregar) => {
        void tokenParaHandshake().then((token) => entregar({ token: token ?? '' }));
      },
    });
    setSocket(s);

    const aoMensagem = (e: EventoMensagemInterna) => {
      void qc.invalidateQueries({ queryKey: ['interno-canais'] });
      // Nada de tocar pelo que eu mesmo escrevi, pelo que já está na minha frente, nem pelo
      // que foi silenciado. Silenciar é "não me chame por isto": o contador continua
      // subindo e a conversa continua na lista — só o toque some.
      const minha = e.mensagem.autor?.tipo === 'usuario' && e.mensagem.autor.id === usuarioId;
      const silenciada = qc
        .getQueryData<CanalResumo[]>(['interno-canais'])
        ?.find((c) => c.id === e.canalId)?.silenciado;
      if (!minha && !silenciada && useCanalAberto.getState().canalId !== e.canalId) {
        tocarAvisoDeMensagem();
      }
    };
    const aoCanal = () => void qc.invalidateQueries({ queryKey: ['interno-canais'] });
    s.on(EVENTO_INTERNO.MENSAGEM, aoMensagem);
    s.on(EVENTO_INTERNO.CANAL, aoCanal);

    return () => {
      s.off(EVENTO_INTERNO.MENSAGEM, aoMensagem);
      s.off(EVENTO_INTERNO.CANAL, aoCanal);
      s.disconnect();
      setSocket(null);
    };
  }, [usuarioId, qc]);

  // O "digitando" de qualquer conversa, para a linha da lista mostrar como no WhatsApp.
  // Fica aqui porque a tela de Conversas só conhece a conversa aberta.
  useEffect(() => {
    if (!socket) return;
    const digitando = (e: EventoDigitandoInterno) =>
      useDigitando.getState().registrar(e.canalId, e.nome);
    socket.on(EVENTO_INTERNO.DIGITANDO, digitando);
    const varredura = setInterval(() => useDigitando.getState().varrer(), 1_000);
    return () => {
      socket.off(EVENTO_INTERNO.DIGITANDO, digitando);
      clearInterval(varredura);
    };
  }, [socket]);

  return (
    <ContextoInterno.Provider value={socket}>
      {children}
      {/* O telefone fica aqui, e não na tela de Conversas: quem recebe uma ligação pode
          estar atendendo cliente, no monitor ou nos relatórios, e precisa ouvir. */}
      <Telefone />
    </ContextoInterno.Provider>
  );
}

/** O socket da conversa interna, para quem precisa falar com o servidor por ele. */
export function useSocketInterno(): Socket | null {
  return useContext(ContextoInterno);
}

/**
 * Ouvir o que chega na conversa interna, de dentro de uma tela.
 *
 * Os ouvintes vão numa `ref` para a tela poder trocar de função a cada render sem
 * desinscrever e reinscrever o socket — e sem perder um evento no meio da troca.
 */
export function useEventosInternos(ouvintes: {
  aoReceberMensagem?: (e: EventoMensagemInterna) => void;
  aoMudarMensagem?: (e: EventoMensagemInterna) => void;
  aoMudarCanal?: (canalId: string) => void;
  aoLer?: (e: EventoLeituraInterna) => void;
  aoDigitar?: (e: EventoDigitandoInterno) => void;
  aoMudarPresenca?: (e: EventoPresencaInterna) => void;
}): void {
  const socket = useContext(ContextoInterno);
  const ref = useRef(ouvintes);
  ref.current = ouvintes;

  useEffect(() => {
    if (!socket) return;
    const mensagem = (e: EventoMensagemInterna) => ref.current.aoReceberMensagem?.(e);
    const mudou = (e: EventoMensagemInterna) => ref.current.aoMudarMensagem?.(e);
    const canal = (e: { canalId: string }) => ref.current.aoMudarCanal?.(e.canalId);
    const lido = (e: EventoLeituraInterna) => ref.current.aoLer?.(e);
    const digitando = (e: EventoDigitandoInterno) => ref.current.aoDigitar?.(e);
    const presenca = (e: EventoPresencaInterna) => ref.current.aoMudarPresenca?.(e);

    socket.on(EVENTO_INTERNO.MENSAGEM, mensagem);
    socket.on(EVENTO_INTERNO.MENSAGEM_MUDOU, mudou);
    socket.on(EVENTO_INTERNO.CANAL, canal);
    socket.on(EVENTO_INTERNO.LIDO, lido);
    socket.on(EVENTO_INTERNO.DIGITANDO, digitando);
    socket.on(EVENTO_INTERNO.PRESENCA, presenca);
    return () => {
      socket.off(EVENTO_INTERNO.MENSAGEM, mensagem);
      socket.off(EVENTO_INTERNO.MENSAGEM_MUDOU, mudou);
      socket.off(EVENTO_INTERNO.CANAL, canal);
      socket.off(EVENTO_INTERNO.LIDO, lido);
      socket.off(EVENTO_INTERNO.DIGITANDO, digitando);
      socket.off(EVENTO_INTERNO.PRESENCA, presenca);
    };
  }, [socket]);
}

/** Avisa que estou escrevendo. A tela segura o volume; aqui é só o envio. */
export function useAvisarQueDigito(): (canalId: string) => void {
  const socket = useContext(ContextoInterno);
  return (canalId: string) => socket?.emit(EVENTO_INTERNO.DIGITANDO, { canalId });
}

/** Quantas mensagens internas estão esperando — é o número que acende no menu. */
export function useNaoLidasInternas(): number {
  const { data } = useQuery({
    queryKey: ['interno-canais'],
    queryFn: internoApi.canais,
    // O WebSocket é quem avisa; este intervalo é só a rede de segurança para a aba que
    // ficou horas aberta e perdeu alguma reconexão.
    refetchInterval: 5 * 60_000,
  });
  return (data ?? ([] as CanalResumo[])).reduce((total, c) => total + c.naoLidas, 0);
}

/** Quanto tempo um aviso de "digitando" vale. A tela manda um a cada três segundos. */
const VALIDADE_DIGITANDO_MS = 4_000;

/** Devolvido quando ninguém digita: um array fixo não dispara redesenho à toa. */
const NINGUEM: string[] = [];

/**
 * Quem está escrevendo, em qualquer conversa.
 *
 * Fica na moldura, e não na tela de Conversas, porque o WhatsApp mostra o "digitando" **na
 * linha da lista** — e para isso é preciso saber de todas as conversas, não só da que está
 * aberta. Guarda nome → até quando o aviso vale: quem para de escrever não manda nada
 * dizendo que parou, e a última tecla é tudo o que se sabe.
 */
export const useDigitando = create<{
  porCanal: Record<string, Record<string, number>>;
  registrar: (canalId: string, nome: string) => void;
  varrer: () => void;
}>((set) => ({
  porCanal: {},
  registrar: (canalId, nome) =>
    set((e) => ({
      porCanal: {
        ...e.porCanal,
        [canalId]: { ...e.porCanal[canalId], [nome]: Date.now() + VALIDADE_DIGITANDO_MS },
      },
    })),
  varrer: () =>
    set((e) => {
      const agora = Date.now();
      const novo: Record<string, Record<string, number>> = {};
      let mudou = false;
      for (const [canalId, pessoas] of Object.entries(e.porCanal)) {
        const vivos = Object.fromEntries(Object.entries(pessoas).filter(([, ate]) => ate > agora));
        if (Object.keys(vivos).length > 0) novo[canalId] = vivos;
        if (Object.keys(vivos).length !== Object.keys(pessoas).length) mudou = true;
      }
      // Devolver o mesmo objeto quando nada venceu evita redesenhar a lista a cada segundo.
      return mudou ? { porCanal: novo } : e;
    }),
}));

/**
 * Os nomes de quem está escrevendo neste canal agora.
 *
 * Assina só o pedaço deste canal, e não o mapa inteiro: com sessenta conversas na lista,
 * assinar o mapa faria cada tecla de qualquer pessoa redesenhar as sessenta linhas.
 */
export function useQuemDigita(canalId: string | null): string[] {
  const pessoas = useDigitando((e) => (canalId ? e.porCanal[canalId] : undefined));
  if (!pessoas) return NINGUEM;
  const agora = Date.now();
  return Object.entries(pessoas)
    .filter(([, ate]) => ate > agora)
    .map(([nome]) => nome);
}
